import { useState, useEffect, useCallback } from 'react';
import {
  Table, TableHead, TableBody, TableRow, Snackbar,
  TableCell, Paper, TablePagination, LinearProgress, Drawer,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import styled from 'styled-components';
import moment from 'moment';

import coreAPI from '../../apis/core';
import { ReactComponent as ArrowDown } from '../../resources/arrow-down.svg';

import CategoryGroupSelector from './CategoryGroupSelector';
import CategoryEditDrawer from './CategoryEditDrawer';

function GroupFilterSelection({ groups }) {
  const trimmedGroups = groups.length > 15
    ? `${groups.slice(0, 12)}...`
    : null;

  return (
    <>
      <br />
      <SelectedGroups>({trimmedGroups || groups})</SelectedGroups>
    </>
  );
}

function mapCategory(category) {
  const {
    web_app: { short_description } = {},
    coming_soon,
    ...restCategory
  } = category;
  return {
    ...restCategory,
    web_app_description: short_description || '',
    coming_soon: coming_soon || false,
  };
}

function Categories() {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [fetching, setFetching] = useState(true);
  const [category, setCategory] = useState(null);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async() => {
      try {
        const categoryGroups = await coreAPI.getCategoryGroups();
        if (categoryGroups) setCategoryGroups(categoryGroups);
      } catch (error) {
        setError(error.message);
      }
    })();
  }, []);

  const syncCategories = useCallback(async() => {
    setFetching(true);
    try {
      const response = await coreAPI.syncCategories();
      const { categories, pagination } = response || {};
      if (categories) setCategories(categories.map(mapCategory));
      if (pagination) {
        setPage(pagination.current_page);
        setTotal(pagination.total);
      }
    } catch (error) {
      setError(error.message);
    }
    setFetching(false);
  }, []);

  const fetchCategories = useCallback(async() => {
    setFetching(true);
    try {
      const response = await coreAPI.getCategories({
        page,
        limit: rowsPerPage,
        include_coming_soon: true,
        ...(typeFilter.length && typeFilter.length !== categoryGroups.length && { type: typeFilter }),
      });
      const { categories, pagination } = response || {};
      if (categories) setCategories(categories.map(mapCategory));
      if (pagination) setTotal(pagination.total);
    } catch (error) {
      setError(error.message);
    }
    setFetching(false);
  }, [page, rowsPerPage, typeFilter, categoryGroups.length]);

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories]);

  const getCategoryGroupTitleById = useCallback((categoryGroupId) => {
    return categoryGroups.find((group) => group._id === categoryGroupId)?.title;
  }, [categoryGroups]);

  function handleChangePage(_, newPage) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(_, event) {
    setRowsPerPage(event.props.value);
  }

  function selectCategory(category) {
    setCategory(category);
  }

  function deselectCategory() {
    setCategory(null);
  }

  async function updateCategory(categoryId, update) {
    try {
      await coreAPI.updateCategory(categoryId, update);
      deselectCategory();
      fetchCategories();
    } catch (error) {
      setError(error.message);
    }
  }

  const updateCategoryGroupSelection = useCallback((selected) => {
    if (!selected.length) return setTypeFilter([]);
    if (selected.length === categoryGroups.length) return setTypeFilter(categoryGroups.map(group => group._id));
    setTypeFilter(selected);
  }, [categoryGroups]);

  return (
    <>
      {fetching && <LinearProgress color='secondary' />}
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={Boolean(error)}
        autoHideDuration={4000}
        message={error}
        onClose={() => setError(null)}
      /> 
      <MainContainer fetching={fetching}>
        <SyncButton onClick={syncCategories} disabled={fetching}>Sync Categories</SyncButton>
        <Table>
          <TableHead>
            <TableRow>
              <HeaderCell>Title</HeaderCell>
              <HeaderCell>Description</HeaderCell>
              <HeaderCellWithDropdown>
                Group <ArrowDown />
                {
                  typeFilter.length > 0
                  && typeFilter.length !== categoryGroups.length
                  && <GroupFilterSelection groups={typeFilter.map(getCategoryGroupTitleById).join(', ')} />
                }
                <div>
                  <CategoryGroupSelector
                    categoryGroups={categoryGroups}
                    selected={typeFilter}
                    updateCategoryGroupSelection={updateCategoryGroupSelection}
                  />
                </div>
              </HeaderCellWithDropdown>
              <HeaderCell>Coming soon</HeaderCell>
              <HeaderCell>Index</HeaderCell>
              <HeaderCell>Last Updated</HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length > 0 && categories.map((category) => (
              <TableRow key={category._id} onClick={() => selectCategory(category)}>
                <TableCell>
                  <CategoryContainer>
                    <ImageContainer>
                      {Boolean(category.image_url)
                        ? <CategoryImage src={category.image_url} alt="category avatar" />
                        : <ImagePlaceholder />}
                    </ImageContainer>
                    {category.title}
                  </CategoryContainer>
                </TableCell>
                <TableCell>{category.description}</TableCell>
                <TableCell>{category.category_groups?.map(getCategoryGroupTitleById)?.join(', ')}</TableCell>
                <TableCell>{category.coming_soon && <CheckIcon fontSize={'large'} nativeColor={'#2f9d6d'} />}</TableCell>
                <TableCell>{category.index}</TableCell>
                <TableCell>{moment(category.date_modified).format('DD/MM/YYYY')}</TableCell>
              </TableRow>
            ))}
            {categories.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">No categories</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 20, 50]}
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </MainContainer>
      <Drawer anchor="right" open={Boolean(category)} onClose={deselectCategory}>
      {category && <CategoryEditDrawer
        category={category}
        categoryGroups={categoryGroups}
        updateCategory={updateCategory}
        closeDrawer={deselectCategory} />}
      </Drawer>
    </>
  );
}

export default Categories;

const MainContainer = styled(Paper)`
  position: relative;
  margin-top: ${({ fetching }) => fetching ? '12px' : '1rem'};
  margin-bottom: 1rem;
`;

const SyncButton = styled.button`
  position: absolute;
  top: -66px;
  right: 0;
  background-color: ${({ disabled}) => disabled ? '#C9C9C9' : '#4DB181'};
  color: white;
  font-weight: 600;
  border-radius: 4px;
  border: 0;
  height: 36px;
  cursor: ${({ disabled}) => disabled ? 'not-allowed' : 'pointer'};
`;

const HeaderCell = styled(TableCell)`
  font-weight: 600 !important;
`;

const HeaderCellWithDropdown = styled(HeaderCell)`
  position: relative;
  cursor: pointer;

  && > div {
    position: absolute;
    top: 50px;
    left: 0;
    display: none;
  }

  &:hover > div {
    display: block;
  }
`;

const CategoryContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ImageContainer = styled.div`
  display: flex;
  width: 55px;
  height: 50px;
  justify-content: middle;
  align-items: center;
`;

const CategoryImage = styled.img`
  margin: auto;
  max-width: 50px;
  max-height: 35px;
`;

const ImagePlaceholder = styled.div`
  width: 50px;
  height: 35px;
  background-color: #C9C9C9;
`;

const SelectedGroups = styled.span`
  white-space: nowrap;
`;
